import React, { useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, ContentCopy, Check } from "@mui/icons-material";
import { copyTextToClipboard } from "../../utils/clipboard";
import InfoTip from "../shared/InfoTip";

const TIPS = {
  reload:
    "Load the latest saved version of this agent. Your unsaved edits are listed above the form so you can copy them back.",
  keepEditing:
    "Close this and keep your edits on screen so you can copy them first. Nothing is saved until you reload.",
  copy: "Copy what you had typed in this field, so you can paste it back after reloading.",
  why:
    "Someone changed this agent after you opened it. Example: you asked Claude to switch the model while this page was open. Saving now would undo that, so saving is paused.",
};

/**
 * Agents can also be changed from AI apps (Platform MCP), so a save built on an older
 * copy is refused with a 409. These two pieces handle that:
 *
 * - `AgentConflictDialog` — shown when the 409 arrives: Reload, or keep
 *   editing (auto-save stays paused) to copy text out first.
 * - `AgentConflictBanner` — stays above the form, listing the fields the user
 *   had changed with a copy button for each text value. While paused it also
 *   offers Reload; after the reload it lists what to re-apply.
 *
 * Nothing is re-applied automatically: that could overwrite exactly the
 * change from Claude the 409 exists to protect.
 */
const AgentConflictDialog = ({ open, message, isDarkMode, reloading, onReload, onKeepEditing }) => (
  <Dialog open={open} onClose={reloading ? undefined : onKeepEditing} maxWidth="sm" fullWidth>
    <DialogTitle
      sx={{
        fontFamily: '"Poppins", sans-serif',
        fontWeight: 600,
        borderBottom: "1px solid",
        borderColor: "divider",
        backgroundColor: isDarkMode ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
      }}
    >
      This agent was changed elsewhere
    </DialogTitle>
    <DialogContent sx={{ pt: "20px !important" }}>
      <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.7 }}>
        {message}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.7 }}>
        For example, it was changed from an AI app such as Claude or ChatGPT while this page was open. Saving is paused
        so that change isn't lost. Reload to get the latest version — your edits will be listed so
        you can copy them back.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <Tooltip title={TIPS.keepEditing} arrow>
        <span>
          <Button onClick={onKeepEditing} disabled={reloading}>
            Keep editing
          </Button>
        </span>
      </Tooltip>
      <Tooltip title={TIPS.reload} arrow>
        <span>
          <Button
            variant="contained"
            onClick={onReload}
            disabled={reloading}
            startIcon={reloading ? <CircularProgress size={15} color="inherit" /> : null}
          >
            {reloading ? "Reloading…" : "Reload"}
          </Button>
        </span>
      </Tooltip>
    </DialogActions>
  </Dialog>
);

const PREVIEW_CHARS = 140;

const preview = (value) => {
  const flat = value.replace(/\s+/g, " ").trim();
  return flat.length > PREVIEW_CHARS ? `${flat.slice(0, PREVIEW_CHARS)}…` : flat;
};

/**
 * @param phase  'paused' (409 seen, not reloaded yet) | 'reloaded'
 * @param fields [{ key, label, value }] — `value` is copyable text, or null for
 *               settings that can only be set again by hand.
 */
export const AgentConflictBanner = ({ phase, fields, reloading, onReload, onDismiss }) => {
  const [copiedKey, setCopiedKey] = useState(null);
  const paused = phase === "paused";

  const handleCopy = async (field) => {
    try {
      await copyTextToClipboard(field.value);
      setCopiedKey(field.key);
      setTimeout(() => setCopiedKey((k) => (k === field.key ? null : k)), 2000);
    } catch (_) {
      setCopiedKey(null);
    }
  };

  return (
    <Alert
      severity={paused ? "warning" : "info"}
      sx={{ m: 1, borderRadius: "10px", flexShrink: 0, "& .MuiAlert-message": { width: "100%" } }}
      action={
        paused ? (
          <Tooltip title={TIPS.reload} arrow>
            <span>
              <Button
                color="inherit"
                size="small"
                onClick={onReload}
                disabled={reloading}
                startIcon={reloading ? <CircularProgress size={13} color="inherit" /> : null}
              >
                Reload
              </Button>
            </span>
          </Tooltip>
        ) : (
          <IconButton size="small" color="inherit" onClick={onDismiss} aria-label="Dismiss">
            <Close fontSize="small" />
          </IconButton>
        )
      }
    >
      <AlertTitle sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 0.75 }}>
        {paused
          ? "Auto-save is paused — this agent was changed elsewhere"
          : "Reloaded the latest version"}
        {paused && <InfoTip title={TIPS.why} />}
      </AlertTitle>
      {fields.length === 0 ? (
        <Typography variant="body2">
          {paused
            ? "You have no unsaved changes. Reload to continue editing."
            : "You had no unsaved changes, so nothing needs re-applying."}
        </Typography>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {paused
              ? "Your unsaved changes are below. Copy what you need, then reload."
              : "Fields you had changed — copy them back where you still want them, then save."}
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 260, overflowY: "auto" }}
          >
            {fields.map((field) => (
              <Box
                component="li"
                key={field.key}
                sx={{ display: "flex", alignItems: "flex-start", gap: 1, justifyContent: "space-between" }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {field.label}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", color: "text.secondary", wordBreak: "break-word" }}
                  >
                    {field.value
                      ? preview(field.value)
                      : field.value === null
                        ? "Changed — set it again after reloading."
                        : "Cleared."}
                  </Typography>
                </Box>
                {field.value && (
                  <Tooltip title={TIPS.copy} arrow>
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleCopy(field)}
                      startIcon={copiedKey === field.key ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                      sx={{ flexShrink: 0, textTransform: "none" }}
                    >
                      {copiedKey === field.key ? "Copied" : "Copy"}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Alert>
  );
};

export default AgentConflictDialog;
